import React, { useCallback, useEffect, useState } from 'react';
import {
  Command, Users, CalendarClock, ClockAlert, Store, DollarSign, ShieldCheck,
  Activity, RefreshCw, Siren, TrendingUp, BrainCircuit, CheckCircle2, XCircle,
} from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';

const fmt = (v) => (typeof v === 'number' ? v.toLocaleString() : v ?? '—');
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function EnterpriseCommandCenter() {
  const [data, setData] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [days, setDays] = useState(30);

  const load = useCallback(async (d = days) => {
    try {
      const [cc, ek] = await Promise.all([
        apiCall('/api/admin/command-center'),
        apiCall(`/api/admin/executive-kpis?days=${d}`),
      ]);
      setData(cc); setKpis(ek);
    } catch (e) { toast.error(e.message); setData({}); setKpis({}); }
  }, [days]);

  useEffect(() => { load(); const t = setInterval(() => load(), 60000); return () => clearInterval(t); }, [load]);

  if (!data || !kpis) return <p className="text-sm text-slate-500 py-8">Loading command center…</p>;
  const { workforce: wf = {}, scheduling: sc = {}, attendance: at = {}, marketplace: mk = {},
    financial: fin = {}, compliance: cp = {}, system: sys = {} } = data;
  const ek = kpis;
  return (
    <div className="space-y-6" data-testid="enterprise-command-center">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-800 to-indigo-700 flex items-center justify-center">
            <Command className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Enterprise Command Center</h2>
            <p className="text-xs text-slate-500">Real-time platform overview · refreshed {data.generated_at?.slice(11, 19)} UTC · auto-refresh 60s</p>
          </div>
        </div>
        <button onClick={() => load()} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-medium hover:bg-slate-200" data-testid="cc-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* system health strip */}
      <div className="bg-slate-900 rounded-xl p-4 flex items-center gap-6 flex-wrap" data-testid="cc-system-strip">
        <span className="text-xs text-slate-300 flex items-center gap-1.5">
          <Activity className={`w-4 h-4 ${sys.db_status === 'healthy' ? 'text-emerald-400' : 'text-amber-400'}`} />
          DB {sys.db_ping_ms}ms · {sys.db_status}
        </span>
        <span className="text-xs text-slate-300">{fmt(sys.notifications_24h)} notifications / 24h</span>
        <span className="text-xs text-slate-300">{fmt(sys.audit_events_24h)} audit events / 24h</span>
        <span className="flex items-center gap-2 ml-auto flex-wrap">
          {(sys.integrations || []).map((i) => (
            <span key={i.name} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${i.configured ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-700 text-slate-400'}`}
              data-testid={`cc-integration-${i.name.replace(/\s/g, '-').toLowerCase()}`}>
              {i.configured ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}{i.name}
            </span>
          ))}
        </span>
      </div>

      {/* overview grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <Section icon={Users} tint="text-indigo-600" title="Workforce" testid="cc-workforce" rows={[
          ['Active workers', `${fmt(wf.active_workers)} / ${fmt(wf.total_workers)}`],
          ['Active employers', fmt(wf.active_employers)],
          ['Active agencies', fmt(wf.active_agencies)],
          ['Verified workers', fmt(wf.verified_workers)],
          ['Pending verification', fmt(wf.pending_verification)],
          ['Available right now', fmt(wf.available_now)],
        ]} />
        <Section icon={CalendarClock} tint="text-violet-600" title="Scheduling" testid="cc-scheduling" rows={[
          ['Open shifts (upcoming)', fmt(sc.open_shifts)],
          ['Filled shifts (upcoming)', fmt(sc.filled_shifts)],
          ['Fill rate', `${sc.fill_rate_pct}%`],
          ['Emergency shifts', <span key="em" className={sc.emergency_shifts ? 'text-red-600 font-bold' : ''}>{fmt(sc.emergency_shifts)}</span>],
          ['Shifts created (30d)', fmt(sc.total_shifts_30d)],
        ]} />
        <Section icon={ClockAlert} tint="text-amber-600" title="Attendance" testid="cc-attendance" rows={[
          ['Check-ins today', fmt(at.checkins_today)],
          ['Check-ins (7d)', fmt(at.checkins_7d)],
          ['Late arrivals (7d)', fmt(at.late_7d)],
          ['No-shows (7d)', fmt(at.no_shows_7d)],
          ['Attendance rate', `${at.attendance_rate_pct}%`],
        ]} />
        <Section icon={Store} tint="text-emerald-600" title="Marketplace & Sharing" testid="cc-marketplace" rows={[
          ['Active partnerships', fmt(mk.active_partnerships)],
          ['Pending invites', fmt(mk.pending_partner_invites)],
          ['Float pools / shared workers', `${fmt(mk.float_pools)} / ${fmt(mk.shared_pool_workers)}`],
          ['Pending sharing requests', fmt(mk.pending_sharing_requests)],
          ['Marketplace orders (30d)', fmt(mk.marketplace_orders_30d)],
        ]} />
        <Section icon={DollarSign} tint="text-green-600" title="Financial" testid="cc-financial" rows={[
          ['Revenue today', money(fin.revenue_today)],
          ['Revenue MTD', money(fin.revenue_mtd)],
          ['Revenue YTD', money(fin.revenue_ytd)],
          ['Outstanding payroll', money(fin.outstanding_payroll)],
          ['Open finance alerts', <span key="al" className={fin.open_alerts ? 'text-amber-600 font-bold' : ''}>{fmt(fin.open_alerts)}</span>],
        ]} />
        <Section icon={ShieldCheck} tint="text-sky-600" title="Compliance & Checks" testid="cc-compliance" rows={[
          ['Background checks', fmt(cp.background_checks_total)],
          ['Checks in progress', fmt(cp.background_checks_pending)],
          ['Roster verifications pending', fmt(cp.roster_verifications_pending)],
        ]} />
      </div>

      {/* executive KPIs */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
          <TrendingUp className="w-4 h-4 text-indigo-600" /> Executive KPIs
        </h3>
        <div className="flex gap-1.5">
          {[30, 90, 365].map((d) => (
            <button key={d} onClick={() => { setDays(d); load(d); }}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium ${days === d ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'}`}
              data-testid={`cc-kpi-days-${d}`}>{d}d</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="cc-executive-kpis">
        <Kpi label="Worker utilization" value={`${ek.workforce?.utilization_pct ?? 0}%`} sub={`${fmt(ek.workforce?.workers_with_assignments)} assigned`} />
        <Kpi label="New workers" value={fmt(ek.workforce?.new_workers)} sub={`of ${fmt(ek.workforce?.total_workers)} total`} />
        <Kpi label="Shift fill rate" value={`${ek.scheduling?.fill_rate_pct ?? 0}%`} sub={`${fmt(ek.scheduling?.filled)} / ${fmt(ek.scheduling?.shifts_in_window)}`} />
        <Kpi label="Avg time to fill" value={`${ek.scheduling?.avg_fill_time_hours ?? 0}h`} sub={`${fmt(ek.scheduling?.rescues_activated)} rescues`} />
        <Kpi label="Attendance rate" value={`${ek.attendance?.attendance_rate_pct ?? 0}%`} sub={`${fmt(ek.attendance?.no_shows)} no-shows`} />
        <Kpi label="Late rate" value={`${ek.attendance?.late_rate_pct ?? 0}%`} sub={`${fmt(ek.attendance?.late_arrivals)} late`} />
        <Kpi label="Shift applications" value={fmt(ek.marketplace?.shifts_picked)} sub="marketplace picks" />
        <Kpi label="Shared workers" value={fmt(ek.marketplace?.shared_workers_approved)} sub={`${fmt(ek.marketplace?.shared_worker_requests)} requests`} />
        <Kpi label="Active programs" value={fmt(ek.operations?.active_programs)} sub={`${fmt(ek.operations?.active_locations)} locations`} />
        <Kpi label="AI auto-assigns" value={fmt(ek.ai?.auto_assignments)} sub={`${fmt(ek.ai?.suggestions)} suggestions`} icon={BrainCircuit} />
        <Kpi label="AI bulk runs" value={fmt(ek.ai?.bulk_runs)} sub={`${fmt(ek.ai?.policy_changes)} policy changes`} />
        <Kpi label="Emergencies opened" value={fmt(ek.scheduling?.emergency_opened)} sub="in window" icon={Siren} />
      </div>
    </div>
  );
}

function Section({ icon: Icon, tint, title, rows, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testid}>
      <p className={`text-xs font-bold uppercase mb-2 flex items-center gap-1.5 ${tint}`}>
        <Icon className="w-4 h-4" /> {title}
      </p>
      <div className="space-y-1.5">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-center justify-between text-sm">
            <span className="text-slate-500 text-xs">{label}</span>
            <span className="font-semibold text-slate-900">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, icon: Icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <p className="text-[11px] text-slate-500 flex items-center gap-1">{Icon && <Icon className="w-3.5 h-3.5" />}{label}</p>
      <p className="text-lg font-bold text-slate-900">{value}</p>
      {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
    </div>
  );
}
